import React, { Suspense, lazy, useEffect, useState } from 'react'
import { useAppDispatch } from '../../store'
import { type ApplyFiltersAndDispatchParams, type Row } from '../../components/CustomTable/types'
import LoadingPage from '../../pages/LoadingPage'
import { getFacultyAndStaffApi } from '../../store/slices/facultyAndStaffSlice/apis'
import { getLoading, getUserList } from '../../store/slices/facultyAndStaffSlice/selectors'
import CreateAndEditFacultyAndStaff from './CreateAndEditFacultyAndStaff'
import FacultyAndStaffTopbarActions from './FacultyAndStaffTopbarActions'
import DeleteFaculty from './DeleteFaculty'
import { notification, closeNotification } from '../../components/Notifications'
import api from '../../utils/api'
import { exportCsv } from '../../utils/exportCsv'
import ExportProcessingModal from '../../components/ExportProcessingModal'
import { ExportBackgroundTaskRunningModal } from '../../components/ExportBackgroundTaskRunningModal'
import { useTaskStatus } from '../../hooks/useTaskStatus'
import { useUserRole } from '../../hooks/useUserRole'
import UserManager from '../../APIManager/UserManager'
import ResetClassrankedAuthConfirmationModal from '../../components/ResetClassrankedAuthConfirmationModal'

const AppTable = lazy(async () => await import('../../components/CustomTable'))

const FacultyAndStaff = (): JSX.Element => {
  const isLoading = getLoading()
  const userList = getUserList()
  const dispatch = useAppDispatch()

  const [params, setParams] = useState<ApplyFiltersAndDispatchParams>({
    pageSize: 15,
    scope: 'my'
  })
  const [loadingExport, setLoadingExport] = useState(false)
  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [exportModalMessage, setExportModalMessage] = useState('')
  const { activeRole } = useUserRole()
  const showResetAction = activeRole?.roleType === 'GLOBAL'
  const [isResetPending, setIsResetPending] = useState(false)
  const [isResetModalOpen, setIsResetModalOpen] = useState(false)

  const {
    taskId: uploadTaskId,
    setTaskId: setUploadTaskId,
    showBackgroundModal: showUploadBlockedModal,
    setShowBackgroundModal: setShowUploadBlockedModal,
    isCheckingStatus: isUploadRunning
  } = useTaskStatus('uploadTask_faculty')

  const {
    taskId: exportTaskId,
    setTaskId: setExportTaskId,
    showBackgroundModal: showExportBlockedModal,
    setShowBackgroundModal: setShowExportBlockedModal,
    isCheckingStatus: isExportRunning,
    checkStatusBeforeExport
  } = useTaskStatus('exportTask_faculty')

  const isAnyTaskRunning = isUploadRunning || isExportRunning

  const openBlockedModal = (): void => {
    if (isUploadRunning) setShowUploadBlockedModal(true)
    else setShowExportBlockedModal(true)
  }

  useEffect(() => {
    void dispatch(getFacultyAndStaffApi({ params }))
  }, [params])

  const columns = userList?.keys ?? []
  const rows: Row[] = userList?.data ?? []
  const tableHeader = userList?.header?.headerName ?? ''
  const allowedActions = userList?.actions ?? []

  const handleExportFaculty = async (): Promise<void> => {
    if (isUploadRunning) { openBlockedModal(); return }
    const canExport = await checkStatusBeforeExport()
    if (!canExport) return
    setLoadingExport(true)
    const toastKey = notification('Preparing download...', { variant: 'loading', persist: true })
    try {
      const exportParams = {
        isFaculty: true,
        search: params?.search || '',
        sort: params?.sort || undefined,
        pageSize: params?.pageSize || 15
      }
      const response = await api.get('/api/users/export/', { params: exportParams })
      if (response?.status === 202) {
        if (response?.data?.taskId) {
          setExportTaskId(response.data.taskId)
        }
        closeNotification(toastKey)
        setExportModalMessage(response?.data?.message || '')
        setExportModalOpen(true)
        return
      }
      const data = response?.data
      if (data) {
        exportCsv({ data, headers: response?.headers, loadingToastKey: toastKey })
      } else {
        closeNotification(toastKey)
      }
    } catch (error: any) {
      closeNotification(toastKey)
      notification(error?.message ?? 'Internal server error', { variant: 'error' })
    } finally {
      setLoadingExport(false)
    }
  }

  const handleExportFacultyRoles = async (): Promise<void> => {
    if (isUploadRunning) { openBlockedModal(); return }
    const canExport = await checkStatusBeforeExport()
    if (!canExport) return
    setLoadingExport(true)
    const toastKey = notification('Preparing download...', { variant: 'loading', persist: true })
    try {
      const exportParams = {
        isAdmin: true,
        search: params?.search || '',
        sort: params?.sort || undefined,
        pageSize: params?.pageSize || 15
      }
      const response = await api.get('/api/users/roles/export/', { params: exportParams })
      if (response?.status === 202) {
        if (response?.data?.taskId) {
          setExportTaskId(response.data.taskId)
        }
        closeNotification(toastKey)
        setExportModalMessage(response?.data?.message || '')
        setExportModalOpen(true)
        return
      }
      const data = response?.data
      if (data) {
        exportCsv({ data, headers: response?.headers, loadingToastKey: toastKey })
      } else {
        closeNotification(toastKey)
      }
    } catch (error: any) {
      closeNotification(toastKey)
      notification(error?.message ?? 'Internal server error', { variant: 'error' })
    } finally {
      setLoadingExport(false)
    }
  }

  const handleResetClassrankedAuth = async (deleteForAll: boolean): Promise<void> => {
    setIsResetPending(true)
    try {
      const response = await UserManager.resetClassrankedAuth({ isFaculty: true, delete_for_all: deleteForAll })
      notification(response?.data?.message ?? 'Cognito reset completed.', { variant: 'success' })
      setIsResetModalOpen(false)
      void dispatch(getFacultyAndStaffApi({ params }))
    } catch (error: any) {
      notification(error?.message ?? 'Reset Classranked Auth failed', { variant: 'error' })
    } finally {
      setIsResetPending(false)
    }
  }

  const isExportDisabled = loadingExport || isAnyTaskRunning
  const topbarActionsDropdownMenu = [
    { text: 'Export Faculty (.csv)', action: () => { void handleExportFaculty() }, disabled: isExportDisabled, visible: params?.scope !== 'all' },
    { text: 'Export Faculty Roles (.csv)', action: () => { void handleExportFacultyRoles() }, disabled: isExportDisabled, visible: params?.scope !== 'all' },
    {
      text: 'Reset Classranked Authentication',
      action: () => { setIsResetModalOpen(true) },
      disabled: isResetPending || isAnyTaskRunning,
      visible: showResetAction
    }
  ]

  return (
    <>
      <Suspense fallback={<LoadingPage />}>
        <AppTable
          topbarTitle={tableHeader}
          customTopbarAction={
            <FacultyAndStaffTopbarActions
              params={params}
              allowedActions={allowedActions}
              setUploadTaskId={setUploadTaskId}
              isTaskRunning={isAnyTaskRunning}
              onTaskBlock={openBlockedModal}
            />
          }
          topbarActionsDropdownMenu={topbarActionsDropdownMenu}
          exportButtonProps={{ loading: isExportDisabled, disabled: isExportDisabled }}
          list={userList}
          rows={rows}
          loading={isLoading}
          columns={columns}
          featureName="Faculty"
          params={params}
          setParams={setParams}
          editAction={<CreateAndEditFacultyAndStaff params={params} isTaskRunning={isAnyTaskRunning} onTaskBlock={openBlockedModal} />}
          deleteAction={<DeleteFaculty params={params} isTaskRunning={isAnyTaskRunning} onTaskBlock={openBlockedModal} />}
          actionModalEntityDisplayField="firstName"
          showScopeToggle={true}
          scopeToggleTitle={tableHeader}
        />
      </Suspense>
      <ExportProcessingModal
        isOpen={exportModalOpen}
        onClose={() => { setExportModalOpen(false) }}
        message={exportModalMessage}
      />
      <ExportBackgroundTaskRunningModal
        isOpen={showExportBlockedModal}
        onClose={() => { setShowExportBlockedModal(false) }}
        taskId={exportTaskId ?? ''}
      />
      <ExportBackgroundTaskRunningModal
        isOpen={showUploadBlockedModal}
        onClose={() => { setShowUploadBlockedModal(false) }}
        taskId={uploadTaskId ?? ''}
      />
      <ResetClassrankedAuthConfirmationModal
        isOpen={isResetModalOpen}
        onClose={() => { setIsResetModalOpen(false) }}
        onConfirm={handleResetClassrankedAuth}
        isSubmitting={isResetPending}
        bodyText='This will remove ClassRanked authentication access for faculty and staff on this tab who do not have any role in the ClassRanked system. Their records will remain in the system, and they can set up access again the next time they log in.'
      />
    </>
  )
}

export default FacultyAndStaff
